import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, apiSend, useAdminState, Erro, Toast } from "../../hooks/useAdminApi";
import { sanitizeForFrame } from "../../utils/sanitize";

/**
 * TEMPLATES — as PÁGINAS DE VENDA (paywall).
 *
 * É o que aparece quando alguém tenta abrir um conteúdo que não assinou: a tela que
 * decide se a pessoa paga ou vai embora. Por isso a tela é VISUAL — o admin reconhece
 * o template pela cara dele, não por um nome numa lista — e o editor mostra a prévia
 * do lado, porque o que importa é o que o cliente vê na hora de decidir.
 *
 * SOBRE EXCLUIR: a API NÃO bloqueia excluir um template em uso (testado: DELETE de um
 * template usado por uma série responde 200). Ela só marca `deleted_at`; a série/conteúdo
 * continua apontando para ele e o GET desse template passa a devolver erro — ou seja, o
 * paywall daquele conteúdo QUEBRA. Como o backend não protege, a proteção é aqui: contamos
 * quem usa e, se houver uso, exigimos o nome digitado antes de deixar excluir.
 */

// O HTML do template é renderizado num <iframe sandbox> (sem allow-scripts). Este estilo
// base só dá o palco (fundo preto, sem margem) — o <style> do próprio template vem depois
// e manda nele.
const ESTILO_BASE = `<style>
  html, body { margin: 0; padding: 0; min-height: 100%; background: #000; color: #fff;
    font-family: Roboto, system-ui, -apple-system, sans-serif; }
  img, video, iframe { max-width: 100%; }
</style>`;

// Esqueleto de um paywall de verdade — começar da folha em branco não ajuda ninguém.
const HTML_INICIAL = `<div style="font-family:Roboto,sans-serif;background:#0e0e10;color:#fff;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:32px;gap:14px;">
  <div style="font-size:30px;font-weight:900;letter-spacing:-.5px;">Reinoplay Premium</div>
  <div style="font-size:15px;opacity:.8;max-width:360px;">Toda a Bíblia em desenhos animados, sem anúncios, para o seu filho.</div>
  <a href="https://reinoplay.online/checkout" style="background:#E50914;color:#fff;border-radius:12px;padding:13px 30px;font-weight:700;font-size:15px;text-decoration:none;">Assinar agora</a>
  <div style="font-size:12px;opacity:.55;">Cancele quando quiser</div>
</div>`;

/** A API fala em `template_*`; o resto da tela fala em português direto. */
const daApi = (t) => ({
  id: t.template_id,
  name: t.template_name,
  html: t.template_html || "",
});

/**
 * Todo HTML de template passa por aqui: iframe COM sandbox e SEM allow-scripts.
 * `sanitizeForFrame` (e não o DOMPurify direto) porque o DOMPurify normal REMOVE os
 * blocos <style> — e um paywall sem o próprio <style> vira texto solto numa página branca.
 */
function Prancha({ html, titulo, style }) {
  return (
    <iframe
      title={titulo}
      sandbox=""
      srcDoc={ESTILO_BASE + sanitizeForFrame(html)}
      style={{ border: "none", display: "block", background: "#000", ...style }}
    />
  );
}

export default function Templates() {
  const { tema, setTema } = useOutletContext();
  const { erro, toast, rodar, limparErro } = useAdminState();

  const [templates, setTemplates] = useState(null);
  const [uso, setUso] = useState(null); // { [id]: { series, conteudos } } — null = não deu pra checar
  const [editor, setEditor] = useState(null); // { id?, name, html }
  const [excluir, setExcluir] = useState(null); // template escolhido pra exclusão

  const carregar = useCallback(
    () =>
      rodar(async () => {
        const r = await apiGet(API_ROUTES.TEMPLATES.list());
        setTemplates((r.templates || []).map(daApi));

        // Contagem de uso é o que torna a exclusão segura, mas não pode DERRUBAR a tela:
        // se séries/conteúdos falharem, seguimos sem o número e avisamos no modal.
        try {
          const [s, c] = await Promise.all([
            apiGet(API_ROUTES.SERIES.list()),
            apiGet(API_ROUTES.CONTENTS.list()),
          ]);
          const mapa = {};
          const contar = (id, chave) => {
            if (!id) return;
            if (!mapa[id]) mapa[id] = { series: 0, conteudos: 0 };
            mapa[id][chave] += 1;
          };
          (s.series || []).forEach((x) => contar(x.serie_template_id, "series"));
          (c.contents || []).forEach((x) => contar(x.content_template_id, "conteudos"));
          setUso(mapa);
        } catch {
          setUso(null);
        }
      }),
    [rodar]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const emUso = useMemo(
    () =>
      uso && templates
        ? templates.filter((t) => uso[t.id] && uso[t.id].series + uso[t.id].conteudos > 0).length
        : null,
    [uso, templates]
  );

  const salvar = ({ id, name, html }) =>
    rodar(
      async () => {
        const corpo = { template_name: name.trim(), template_html: html };
        if (id) await apiSend(API_ROUTES.TEMPLATES.update(id), "PUT", corpo);
        else await apiSend(API_ROUTES.TEMPLATES.create(), "POST", corpo);
        setEditor(null);
        await carregar();
      },
      id ? "Template salvo." : "Template criado."
    );

  const remover = (t) =>
    rodar(async () => {
      try {
        await apiSend(API_ROUTES.TEMPLATES.delete(t.id), "DELETE");
      } finally {
        // Fecha SEMPRE: se a API recusar, é a mensagem dela que o admin precisa ler,
        // e ela fica atrás do modal.
        setExcluir(null);
      }
      await carregar();
    }, "Template excluído.");

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Templates"
        subtitulo="Páginas de venda que aparecem quando alguém tenta abrir um conteúdo que não assinou."
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <span style={{ fontSize: 13, color: "var(--text2)" }}>
            {templates
              ? `${templates.length} ${templates.length === 1 ? "template" : "templates"}${
                  emUso === null ? "" : ` · ${emUso} em uso`
                }`
              : " "}
          </span>
          <button
            className="adm__btn"
            onClick={() => setEditor({ name: "", html: HTML_INICIAL })}
          >
            + Novo template
          </button>
        </div>

        {!templates && <p className="adm__vazio">Carregando…</p>}

        {templates && templates.length === 0 && (
          <div className="adm__card">
            <div className="adm__vazio">
              <strong style={{ color: "var(--text2)", display: "block", marginBottom: 5 }}>
                Nenhum template cadastrado
              </strong>
              Sem página de venda, quem esbarra num conteúdo pago não tem como assinar.
            </div>
          </div>
        )}

        {templates && templates.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 18,
            }}
          >
            {templates.map((t) => {
              const u = uso && uso[t.id];
              const total = u ? u.series + u.conteudos : 0;
              return (
                <div
                  key={t.id}
                  className="adm__card"
                  onClick={() => setEditor({ id: t.id, name: t.name, html: t.html })}
                  style={{ overflow: "hidden", cursor: "pointer" }}
                  title="Abrir editor"
                >
                  {/* MINIATURA RENDERIZADA: o admin reconhece o paywall pelo visual.
                      O iframe é 3x a largura do card e reduzido a 1/3 — assim o HTML
                      "enxerga" uma tela de desktop (~750px) e não um celular estreito. */}
                  <div
                    style={{
                      height: 140,
                      overflow: "hidden",
                      background: "#000",
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    <Prancha
                      html={t.html}
                      titulo={`Prévia do template ${t.name}`}
                      style={{
                        width: "300%",
                        height: 420,
                        transform: "scale(.3333)",
                        transformOrigin: "top left",
                        pointerEvents: "none", // o card inteiro continua clicável
                      }}
                    />
                  </div>

                  <div
                    style={{
                      padding: "12px 14px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 700,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.name}
                      </div>
                      <span
                        className={`adm__chip ${total ? "adm__chip--blue" : ""}`}
                        style={{ marginTop: 6 }}
                      >
                        {uso === null
                          ? "uso desconhecido"
                          : total === 0
                          ? "sem uso"
                          : `${u.series} série(s) · ${u.conteudos} conteúdo(s)`}
                      </span>
                    </div>
                    <button
                      className="adm__btnGhost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExcluir(t);
                      }}
                      style={{
                        flex: "0 0 auto",
                        padding: "5px 10px",
                        color: "var(--cancel)",
                        borderColor: "rgba(255,90,95,.35)",
                      }}
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editor && (
        <Editor inicial={editor} onFechar={() => setEditor(null)} onSalvar={salvar} />
      )}

      {excluir && (
        <ConfirmarExclusao
          template={excluir}
          uso={uso ? uso[excluir.id] || { series: 0, conteudos: 0 } : null}
          onCancelar={() => setExcluir(null)}
          onOk={() => remover(excluir)}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

/** Editor: HTML de um lado, o que o cliente vai ver do outro. */
function Editor({ inicial, onFechar, onSalvar }) {
  const [name, setName] = useState(inicial.name);
  const [html, setHtml] = useState(inicial.html);
  const [previa, setPrevia] = useState(inicial.html);

  // A prévia recarrega o iframe inteiro; fazer isso a cada tecla faz a página de venda
  // piscar e atrapalha quem está escrevendo. 400ms depois da última tecla basta.
  useEffect(() => {
    const t = setTimeout(() => setPrevia(html), 400);
    return () => clearTimeout(t);
  }, [html]);

  const valido = name.trim().length > 0;

  return (
    <div className="adm__modalFundo" onClick={onFechar}>
      <div
        className="adm__modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1000px, 100%)",
          height: "min(640px, 90vh)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <input
            className="adm__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do template (ex.: Paywall padrão)"
            style={{ flex: 1, fontWeight: 700 }}
          />
          <button className="adm__btnGhost" onClick={onFechar}>
            Cancelar
          </button>
          <button
            className="adm__btn"
            disabled={!valido}
            onClick={() => onSalvar({ id: inicial.id, name, html })}
          >
            {inicial.id ? "Salvar" : "Criar template"}
          </button>
        </div>

        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            minHeight: 0,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div className="adm__campo" style={{ marginBottom: 2 }}>
              <label>HTML</label>
            </div>
            <textarea
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              spellCheck="false"
              className="adm__input"
              style={{
                flex: 1,
                minHeight: 0,
                resize: "none",
                fontFamily: '"Roboto Mono", ui-monospace, monospace',
                fontSize: 12.5,
                lineHeight: 1.6,
              }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div className="adm__campo" style={{ marginBottom: 2 }}>
              <label>Prévia ao vivo</label>
            </div>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                border: "1px solid var(--border)",
                borderRadius: 10,
                overflow: "hidden",
                background: "#000",
              }}
            >
              <Prancha
                html={previa}
                titulo="Prévia ao vivo da página de venda"
                style={{ width: "100%", height: "100%" }}
              />
            </div>
            {!html.trim() && (
              <p style={{ fontSize: 11.5, color: "var(--orange)", margin: "8px 0 0" }}>
                Um template vazio mostra uma página em branco para quem tenta assinar.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Excluir um template EM USO quebra o paywall das séries/conteúdos que apontam pra ele —
 * e a API deixa passar. Então o freio é aqui: mostramos quantos dependem dele e, se houver
 * algum, só liberamos depois do nome digitado.
 */
function ConfirmarExclusao({ template, uso, onCancelar, onOk }) {
  const [digitado, setDigitado] = useState("");

  const total = uso ? uso.series + uso.conteudos : 0;
  const perigoso = uso === null || total > 0; // uso desconhecido = trate como perigoso
  const exigir = perigoso ? template.name : null;
  const ok = !exigir || digitado.trim().toLowerCase() === exigir.trim().toLowerCase();

  return (
    <div className="adm__modalFundo" onClick={onCancelar} style={{ zIndex: 200 }}>
      <div
        className="adm__modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(460px, 100%)" }}
      >
        <h3 style={{ color: "var(--cancel)" }}>Excluir template</h3>

        {uso === null ? (
          <p className="adm__modalHint">
            <strong style={{ color: "var(--orange)" }}>
              Não foi possível verificar quem usa este template.
            </strong>{" "}
            Se alguma série ou conteúdo usar “{template.name}” como página de venda, o
            paywall deles para de carregar depois desta exclusão.
          </p>
        ) : total > 0 ? (
          <p className="adm__modalHint">
            <strong style={{ color: "var(--cancel)" }}>
              Este template é usado por {uso.series}{" "}
              {uso.series === 1 ? "série" : "séries"} e {uso.conteudos}{" "}
              {uso.conteudos === 1 ? "conteúdo" : "conteúdos"}.
            </strong>{" "}
            Excluí-lo quebraria o paywall desses itens — o cliente clicaria em “assinar” e
            nada aconteceria. Reaponte-os para outro template antes.{" "}
            <strong>O servidor vai recusar</strong> enquanto houver alguém usando.
          </p>
        ) : (
          <p className="adm__modalHint">
            Nenhuma série ou conteúdo usa “{template.name}” hoje. A exclusão é segura.
          </p>
        )}

        {exigir && (
          <div className="adm__campo">
            <label>
              Para confirmar, digite <code className="adm__code">{exigir}</code>
            </label>
            <input
              className="adm__input"
              value={digitado}
              onChange={(e) => setDigitado(e.target.value)}
            />
          </div>
        )}

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onCancelar}>
            Cancelar
          </button>
          <button className="adm__btn adm__btnDanger" disabled={!ok} onClick={onOk}>
            Excluir template
          </button>
        </div>
      </div>
    </div>
  );
}
